/**
 * Composable для управления таймерами заказов
 * 
 * Отвечает за:
 * - Создание и управление таймерами для каждого заказа
 * - Подсчет оставшегося времени до истечения оплаты
 * - Форматирование времени для отображения
 * - Определение срочности (normal/urgent/critical)
 * - Автоматическую очистку неактуальных таймеров
 */

import { reactive, type Ref } from 'vue'
import { consola } from 'consola'
import { TIMER_CONFIG , TIMER_URGENCY_THRESHOLDS } from '@shared/config'
import { TIMER_EXCLUDED_PAYMENT_STATUSES } from '@entities/order'

import type { OrderSummary } from '@entities/order'

const logger = consola.withTag('order-timers')

/**
 * Состояние таймера для одного заказа
 */
interface OrderTimerState {
  remainingTime: number
  intervalId: NodeJS.Timeout | null
  createdAt: string
}

/**
 * Классы срочности таймера
 */
export type TimerUrgencyClass = 
  | 'order-widget__timer--normal'
  | 'order-widget__timer--urgent'
  | 'order-widget__timer--critical'
  | 'order-widget__timer--expired'

/**
 * Composable для управления таймерами заказов
 */
export function useOrderTimers(navigationOrders: Ref<OrderSummary[]>) {
  // Reactive Map для хранения состояния всех таймеров
  const orderTimers = reactive<Map<string, OrderTimerState>>(new Map())

  /**
   * Вычисляет оставшееся время для заказа на основе createdAt
   */
  function calculateRemainingTime(createdAt: string): number {
    if (!createdAt) return 0

    const createdAtDate = new Date(createdAt)
    const elapsed = Date.now() - createdAtDate.getTime()
    return Math.max(0, TIMER_CONFIG.PAYMENT_TIMEOUT_MS - elapsed)
  }

  /**
   * Форматирует миллисекунды в читаемый формат времени
   * 
   * @param ms - время в миллисекундах
   * @returns отформатированная строка (MM:SS или Xч Xм)
   */
  function formatTime(ms: number): string {
    if (!ms || ms <= 0) return '00:00'

    const totalMinutes = Math.floor(ms / TIMER_CONFIG.TIME_DIVISORS.MINUTE)
    const seconds = Math.floor((ms % TIMER_CONFIG.TIME_DIVISORS.MINUTE) / TIMER_CONFIG.TIME_DIVISORS.SECOND)

    // Если больше часа, показываем часы и минуты
    if (totalMinutes >= TIMER_CONFIG.TIME_DIVISORS.HOUR) {
      const hours = Math.floor(totalMinutes / TIMER_CONFIG.TIME_DIVISORS.HOUR)
      const minutes = totalMinutes % TIMER_CONFIG.TIME_DIVISORS.HOUR
      return `${hours}ч ${minutes}м`
    }

    return `${totalMinutes}:${seconds.toString().padStart(2, '0')}`
  }

  /**
   * Создает и запускает таймер для заказа
   */
  function createOrderTimer(orderId: string, createdAt: string): void {
    if (!orderId || !createdAt) {
      logger.warn('Missing orderId or createdAt when creating timer')
      return
    }

    // Останавливаем существующий таймер если есть
    stopOrderTimer(orderId)

    const remaining = calculateRemainingTime(createdAt)

    const timerState: OrderTimerState = {
      remainingTime: remaining,
      intervalId: null,
      createdAt,
    }

    // Если время уже истекло, не запускаем интервал
    if (remaining <= 0) {
      orderTimers.set(orderId, timerState)
      return
    }

    // Сохраняем состояние
    orderTimers.set(orderId, timerState)

    // Запускаем интервал обновления
    timerState.intervalId = setInterval(() => {
      const currentState = orderTimers.get(orderId)
      if (!currentState) {
        // Таймер был удален, останавливаем интервал
        if (timerState.intervalId) {
          clearInterval(timerState.intervalId)
        }
        return
      }

      if (currentState.remainingTime > 0) {
        currentState.remainingTime = Math.max(
          0,
          currentState.remainingTime - TIMER_CONFIG.UPDATE_INTERVAL_MS,
        )
      } else {
        // Время истекло, останавливаем таймер
        stopOrderTimer(orderId)
      }
    }, TIMER_CONFIG.UPDATE_INTERVAL_MS)
  }

  /**
   * Останавливает таймер для заказа
   */
  function stopOrderTimer(orderId: string): void {
    const timerState = orderTimers.get(orderId)
    if (timerState?.intervalId) {
      clearInterval(timerState.intervalId)
      timerState.intervalId = null
    }
  }

  /**
   * Удаляет таймер для заказа полностью
   */
  function removeOrderTimer(orderId: string): void {
    stopOrderTimer(orderId)
    orderTimers.delete(orderId)
  }

  /**
   * Получает отображаемое время для заказа
   */
  function getTimerDisplay(orderSummary: OrderSummary): string {
    if (!orderSummary?.id) return '00:00'

    const timerState = orderTimers.get(String(orderSummary.id))
    if (timerState) {
      return formatTime(timerState.remainingTime)
    }

    // Если таймера еще нет, вычисляем время на лету
    if (orderSummary.createdAt) {
      const remaining = calculateRemainingTime(orderSummary.createdAt)
      return formatTime(remaining)
    }

    return '00:00'
  }

  /**
   * Получает класс срочности таймера на основе оставшегося времени
   */
  function getTimerUrgencyClass(orderSummary: OrderSummary): TimerUrgencyClass {
    if (!orderSummary?.id) return 'order-widget__timer--normal'

    const timerState = orderTimers.get(String(orderSummary.id))
    const remaining = timerState?.remainingTime ?? 
      (orderSummary.createdAt ? calculateRemainingTime(orderSummary.createdAt) : 0)

    if (remaining <= 0) return 'order-widget__timer--expired'

    const remainingMinutes = Math.floor(remaining / TIMER_CONFIG.TIME_DIVISORS.MINUTE)

    if (remainingMinutes <= TIMER_URGENCY_THRESHOLDS.CRITICAL) {
      return 'order-widget__timer--critical'
    }
    if (remainingMinutes <= TIMER_URGENCY_THRESHOLDS.URGENT) {
      return 'order-widget__timer--urgent'
    }

    return 'order-widget__timer--normal'
  }

  /**
   * Проверяет, нужно ли показывать таймер для заказа
   */
  function shouldShowTimer(orderSummary: OrderSummary): boolean {
    if (!orderSummary) return false
    return !TIMER_EXCLUDED_PAYMENT_STATUSES.has(orderSummary.paymentStatus) && 
           !!orderSummary.createdAt
  }

  /**
   * Инициализирует таймеры для всех неоплаченных заказов
   */
  function initializeTimers(): void {
    for (const orderSummary of navigationOrders.value) {
      const orderId = String(orderSummary.id)

      // Создаем таймер только для неоплаченных заказов с createdAt
      if (shouldShowTimer(orderSummary) && !orderTimers.has(orderId)) {
        createOrderTimer(orderId, orderSummary.createdAt!)
      }
    }
  }

  /**
   * Очищает таймеры для заказов, которых больше нет в списке
   */
  function cleanupOrphanedTimers(): void {
    const activeOrderIds = new Set(navigationOrders.value.map((o) => String(o.id)))

    for (const [orderId] of orderTimers.entries()) {
      if (!activeOrderIds.has(orderId)) {
        removeOrderTimer(orderId)
      }
    }
  }

  /**
   * Останавливает и удаляет все таймеры
   */
  function cleanupAllTimers(): void {
    for (const [orderId] of orderTimers.entries()) {
      removeOrderTimer(orderId)
    }
    orderTimers.clear()
  }

  return {
    // State
    orderTimers,

    // Methods
    calculateRemainingTime,
    formatTime,
    createOrderTimer,
    stopOrderTimer,
    removeOrderTimer,
    getTimerDisplay,
    getTimerUrgencyClass,
    shouldShowTimer,
    initializeTimers,
    cleanupOrphanedTimers,
    cleanupAllTimers,
  }
}

